// Offline mode shim — replaces all Realtime Database operations with in-memory
// state hydrated from a static apps.json snapshot. Activated ONLY when the URL
// hash contains 'offline' (e.g. `zebula.net/mat501#offline`); when inactive,
// every export below is harmless dead code (callers in script.js gate every
// invocation on the OFFLINE constant first).
//
// Survival mode for an RTDB outage: students get one 40-minute classroom
// session with a static lesson set, local input, local validation. Nothing
// uploads, nothing persists past the tab. See claude-context for design notes.

export const OFFLINE = typeof location !== 'undefined' && location.hash.toLowerCase().includes('offline');

// Lazy-bound to z (set by bootOffline). Avoids a circular import with script.js.
let zOffline = null;

// In-memory listener registry. Each entry: { id, type, segs, callback }.
// Callbacks fire via queueMicrotask so async semantics match online (mostly).
const listeners = [];

// localStorage persistence — survives reloads but not tab close on a private
// session. Only the dynamic subtrees (students, teachers) are persisted; the
// static apps/svg/codes are always re-derived from the bundled JSON files.
const STORAGE_KEY = 'zebula-offline-state';
let saveTimer = null;
function scheduleSave() {
  if (!zOffline) return;
  if (saveTimer) return; // already scheduled — coalesce bursts
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      const snapshot = { students: zOffline.students, teachers: zOffline.teachers };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (e) { /* quota / private mode / serialization error — best-effort */ }
  }, 400);
}
function restoreFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { return null; }
}

// --- Path helpers ----------------------------------------------------------

function pathSegs(p) {
  if (Array.isArray(p)) return p.filter(s => s !== '' && s != null);
  return String(p).replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
}

function readOffline(path) {
  const segs = pathSegs(path);
  let cur = zOffline;
  for (const s of segs) {
    if (cur == null) return undefined;
    cur = cur[s];
  }
  return cur;
}

function writeOffline(path, val) {
  const segs = pathSegs(path);
  if (!segs.length) return;
  let cur = zOffline;
  for (let i = 0; i < segs.length - 1; i++) {
    const s = segs[i];
    if (cur[s] == null || typeof cur[s] !== 'object') cur[s] = {};
    cur = cur[s];
  }
  const last = segs[segs.length - 1];
  if (val === null || val === undefined) delete cur[last];
  else cur[last] = val;
}

function isAncestor(ancestor, descendant) {
  if (ancestor.length > descendant.length) return false;
  for (let i = 0; i < ancestor.length; i++) {
    if (ancestor[i] !== descendant[i]) return false;
  }
  return true;
}

function fakeSnapshot(segs, val) {
  return {
    key: segs[segs.length - 1],
    val: () => (val === undefined ? null : val),
    exists: () => val !== undefined && val !== null,
  };
}

// Given a write at writeSegs (with `before` = pre-write value at writeSegs),
// fire every listener whose subscription path is affected.
function fireListeners(writeSegs, before) {
  if (!listeners.length) return;
  const after = readOffline(writeSegs);
  // Snapshot so mid-dispatch (un)subscribes don't corrupt iteration.
  const ls = listeners.slice();
  for (const l of ls) {
    if (l.type === 'value') {
      // Fire when the write touched l's subtree (write at/under l) OR when l
      // is a descendant of the write (l's value bubbled up via parent overwrite).
      const lAffected = isAncestor(l.segs, writeSegs) || isAncestor(writeSegs, l.segs);
      if (!lAffected) continue;
      const lVal = readOffline(l.segs);
      // Compute the "before" value at l's path. If the write was at/under l,
      // l's before is reconstructed by overlaying `before` onto a snapshot of
      // l's after with the write reverted. For simplicity (and because most
      // value-listener consumers only care about the after), pass a deep clone
      // of the current value as the before — same as Firebase's cached prev.
      const lBefore = isAncestor(writeSegs, l.segs)
        ? extractAtPath(before, writeSegs, l.segs)
        : (l.lastVal !== undefined ? l.lastVal : null);
      l.lastVal = lVal;
      queueMicrotask(() => {
        if (!listeners.includes(l)) return; // detached during dispatch
        try { l.invoke(fakeSnapshot(l.segs, lVal), lBefore); } catch (e) { /* swallow */ }
      });
    } else if (l.segs.length === writeSegs.length - 1 && isAncestor(l.segs, writeSegs)) {
      // child_* listener at parent of the write
      const beforeChild = before;
      const afterChild = after;
      const fire = () => {
        if (!listeners.includes(l)) return;
        try {
          // Pass beforeChild as the second argument so script.js's writeFn-
          // computed "before" is overridden with the real pre-write value.
          // (writeFn itself reads from z which has already been mutated by
          // the offline write that triggered this dispatch — its read would
          // return the AFTER value, breaking handlers like fanStats that
          // skip "no change" indices.)
          l.invoke(fakeSnapshot(writeSegs, l.type === 'child_removed' ? beforeChild : afterChild), beforeChild);
        } catch (e) { /* swallow */ }
      };
      if (l.type === 'child_added' && (beforeChild === undefined || beforeChild === null) && afterChild !== undefined && afterChild !== null) {
        queueMicrotask(fire);
      } else if (l.type === 'child_changed' && beforeChild !== undefined && beforeChild !== null && afterChild !== undefined && afterChild !== null && JSON.stringify(beforeChild) !== JSON.stringify(afterChild)) {
        queueMicrotask(fire);
      } else if (l.type === 'child_removed' && beforeChild !== undefined && beforeChild !== null && (afterChild === undefined || afterChild === null)) {
        queueMicrotask(fire);
      }
    }
  }
}

// Given the pre-write value at writeSegs and a descendant path lSegs, return
// what was at lSegs before the write. Used to compute "before" snapshots for
// value listeners deep beneath a parent write.
function extractAtPath(beforeVal, writeSegs, lSegs) {
  if (beforeVal == null) return null;
  let cur = beforeVal;
  for (let i = writeSegs.length; i < lSegs.length; i++) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = cur[lSegs[i]];
  }
  return cur === undefined ? null : cur;
}

// --- Wrapper offline implementations ---------------------------------------
// Each mirrors the return shape of its online counterpart in script.js so
// callers don't need to know which mode they're in.

export function getOffline(safeRef, options = {}) {
  const { cache } = options;
  const val = readOffline(safeRef);
  // Online safeGet returns the value at the deepest path segment (not the
  // raw snapshot). Match by returning the value directly; if undefined,
  // return null (Firebase convention for "no data here").
  return val === undefined ? null : val;
}

export function updateOffline(safeRef, val) {
  // Multi-path write
  if (safeRef === '/' && val && typeof val === 'object') {
    for (const subPath in val) {
      const segs = pathSegs(subPath);
      const before = readOffline(segs);
      writeOffline(segs, val[subPath]);
      fireListeners(segs, before);
    }
    scheduleSave();
    return { key: 'multi-path', ref: safeRef, val };
  }
  const segs = pathSegs(safeRef);
  const before = readOffline(segs);
  // Mirror online safeUpdate's merge-into-existing semantics:
  //   write(z, path, Object.assign(read(z, path), transformed))
  if (val && typeof val === 'object' && !Array.isArray(val) && before && typeof before === 'object' && !Array.isArray(before)) {
    writeOffline(segs, Object.assign({}, before, val));
  } else {
    writeOffline(segs, val);
  }
  fireListeners(segs, before);
  scheduleSave();
  return { key: segs[segs.length - 1], ref: safeRef, val };
}

export function setOffline(safeRef, val) {
  const segs = pathSegs(safeRef);
  const before = readOffline(segs);
  writeOffline(segs, val);
  fireListeners(segs, before);
  scheduleSave();
  return { key: segs[segs.length - 1], ref: safeRef, val };
}

export function removeOffline(safeRef) {
  const segs = pathSegs(safeRef);
  const before = readOffline(segs);
  writeOffline(segs, undefined);
  fireListeners(segs, before);
  scheduleSave();
}

let pushCounter = 0;
export function pushOffline(safeRef, val) {
  // Firebase push IDs are 20 chars. Locally we just need uniqueness within session.
  const id = '-OFFLINE_' + (Date.now().toString(36)) + (pushCounter++).toString(36);
  const segs = pathSegs(safeRef + '/' + id);
  writeOffline(segs, val);
  fireListeners(segs, undefined);
  scheduleSave();
  return Promise.resolve({ key: id, ref: `${safeRef}/${id}`, val });
}

export function transactionOffline(safeRef, fn) {
  const segs = pathSegs(safeRef);
  const before = readOffline(segs);
  const next = fn(before === undefined ? null : before);
  if (next !== undefined) {
    writeOffline(segs, next);
    fireListeners(segs, before);
    scheduleSave();
  }
  return Promise.resolve(next);
}

// Listener subscribe — invoked from script.js's subscribe() in offline mode.
// Mirrors the online subscribe()'s contract: returns an off() function.
//
// Note on writeFn: the online writeFn echoes the snapshot value into z so the
// local cache stays in sync with Firebase. In OFFLINE mode, z IS the source of
// truth — we already mutated it before calling fireListeners — so writeFn would
// be a no-op at best and a bug at worst (it reads `before` from z, which now
// holds the AFTER value, and returns that wrong "before" to userFunc as the
// second argument). We bypass writeFn entirely and pass the real `before` that
// fireListeners captured pre-write.
export function subscribeOffline({ type, path, writeFn, userFunc, initialReplay }) {
  const segs = pathSegs(path);
  const id = Symbol('offline-listener');
  const listener = {
    id,
    type,
    segs,
    invoke: (snapshot, beforeVal) => {
      if (typeof userFunc === 'function') {
        try { userFunc(snapshot, beforeVal); } catch (e) { /* swallow */ }
      }
    },
  };
  listeners.push(listener);
  // Initial replay — match Firebase's "fire once with current state on attach":
  if (type === 'value') {
    const val = readOffline(segs);
    listener.lastVal = val;
    queueMicrotask(() => {
      if (!listeners.includes(listener)) return;
      listener.invoke(fakeSnapshot(segs, val), null);
    });
  } else if (type === 'child_added') {
    const parent = readOffline(segs);
    if (parent && typeof parent === 'object' && !Array.isArray(parent)) {
      for (const childKey of Object.keys(parent)) {
        const childSegs = [...segs, childKey];
        const childVal = parent[childKey];
        queueMicrotask(() => {
          if (!listeners.includes(listener)) return;
          listener.invoke(fakeSnapshot(childSegs, childVal), null);
        });
      }
    }
  }
  return () => {
    const idx = listeners.findIndex(l => l.id === id);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

// --- Bootstrap -------------------------------------------------------------

// Hydrate z with the static apps snapshot + stub identity, so the rest of the
// app can run as if it were a normal student session. Called from index.js
// only when OFFLINE is true.
export async function bootOffline(z) {
  zOffline = z;
  // CSS hook — `html.offline ...` selectors can target offline-mode-only
  // styling (e.g. hiding the toolbar score, since points don't persist).
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.classList.add('offline');
  }
  let apps = null, svg = null;
  try {
    const [appsResp, svgResp] = await Promise.all([
      fetch('apps.json', { cache: 'force-cache' }),
      fetch('svg.json', { cache: 'force-cache' }),
    ]);
    if (appsResp.ok) apps = await appsResp.json();
    if (svgResp.ok) svg = await svgResp.json();
  } catch (e) { /* fall through */ }
  if (!apps) throw new Error('Offline mode could not load apps.json');
  if (!svg) throw new Error('Offline mode could not load svg.json');
  z.apps = apps;
  z.svg = svg;
  // Restore prior session's writes (input, stats, roster, session state) BEFORE
  // the identity stubs below — the stubs use `||=` semantics so they only fill
  // in missing keys, leaving restored data intact. Survives reloads.
  const restored = restoreFromStorage();
  if (restored) {
    if (restored.students) z.students = restored.students;
    if (restored.teachers) z.teachers = restored.teachers;
  }
  // Build a /codes lookup table so existing `get('/codes/{code}/app')` calls
  // (which the boot path uses to figure out which app handles the URL code)
  // resolve naturally to z.codes — same shape RTDB had.
  // Skip non-lesson top-levels: `keys` is per-slide data (key ID → slide
  // fields, not a lesson code), `rules` is an answer-grading rule array.
  z.codes = z.codes || {};
  const LESSON_TYPES = ['math', 'jeopardy'];
  for (const appName of LESSON_TYPES) {
    if (!apps[appName] || typeof apps[appName] !== 'object') continue;
    for (const code of Object.keys(apps[appName])) {
      if (typeof apps[appName][code] === 'object') {
        z.codes[code] = { app: appName, teacher: 'offline-teacher' };
      }
    }
  }
  // Stub identity — every read/write the app performs against /students/X or
  // /teachers/Y now resolves locally without ever hitting Firebase.
  const teacher = 'offline-teacher';
  const student = 'offline-student';
  z.teacher = teacher;
  z.student = student;
  z.uid = student;
  z.name = 'Student';
  // Green color scheme (values pulled verbatim from the colors array in
  // index.js so every --student-color-* CSS variable resolves correctly).
  z.color = z.color || { name: 'green', value: '#73e699', mid: '#32a658', dark: '#0a6629', darker: '#084d1f', darkest: '#001a09', neighbor: '#826b07', blend: '0%' };
  z.emoji = z.emoji || { name: 'apple' };
  // Push the per-student CSS variables that the hash-login branch normally
  // sets — without these, every `var(--student-color-*)` in the stylesheets
  // resolves to nothing and the UI loses its theme color.
  if (typeof document !== 'undefined' && document.documentElement) {
    for (const key of Object.keys(z.color)) document.documentElement.style.setProperty(`--student-color-${key}`, z.color[key]);
  }
  z.teachers = z.teachers || {};
  z.teachers[teacher] = z.teachers[teacher] || {
    online: {},
    settings: { color: 'teal' },
    roster: { [student]: { name: z.name, color: z.color, emoji: z.emoji, status: 'active', type: 'async' } },
    models: {},
  };
  z.students = z.students || {};
  z.students[student] = z.students[student] || {
    settings: { audio: false, log: false, stats: false },
    teacher,
    input: {},
    stats: { keys: {} },
    name: z.name,
    color: z.color,
    emoji: z.emoji,
  };
  // Mark connected so `.info/connected` listeners (App.connect, etc.) report
  // true. The literal '.info' key matches Firebase's magic path; pathSegs
  // treats the leading dot as part of the key, not as a delimiter.
  z['.info'] = z['.info'] || {};
  z['.info'].connected = true;
}
